import * as RadixTabs from '@radix-ui/react-tabs'
import {
  Children,
  createContext,
  forwardRef,
  Fragment,
  isValidElement,
  useContext,
  useState,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/cn'
import { useReorderable, type ReorderItemProps } from '@/hooks/useReorderable'
import { readRememberedTab, rememberTab } from './rememberedTab'
import styles from './Tabs.module.scss'

/** The ``persistKey`` of the enclosing ``TabsRoot``, for its ``TabsList``. */
const PersistKeyContext = createContext<string | undefined>(undefined)

export interface TabsRootProps extends ComponentPropsWithoutRef<typeof RadixTabs.Root> {
  /**
   * Names this set of tabs, once per use — two screens never share a key.
   * The tab the user picks is saved in localStorage under
   * ``prism.tabs.<persistKey>.active``, and the ``TabsList`` inside lets the
   * tabs be dragged into another order under the same key (unless it passes
   * its own ``reorderKey``).
   *
   * Uncontrolled (``defaultValue``): opens on the saved tab. Controlled
   * (``value``, e.g. from the URL): the parent decides what "no choice yet"
   * means, so it reads the saved tab with ``readRememberedTab`` and falls
   * back to its default.
   */
  persistKey?: string
}

export const TabsRoot = forwardRef<HTMLDivElement, TabsRootProps>(function TabsRoot(
  { persistKey, value, defaultValue, onValueChange, children, ...rest },
  ref,
) {
  // Read once on mount: a saved tab only chooses where an uncontrolled set opens.
  const [initial] = useState(() =>
    value === undefined && persistKey ? (readRememberedTab(persistKey) ?? defaultValue) : defaultValue,
  )
  return (
    <RadixTabs.Root
      ref={ref}
      {...(value === undefined ? {} : { value })}
      {...(initial === undefined ? {} : { defaultValue: initial })}
      onValueChange={(next) => {
        if (persistKey) rememberTab(persistKey, next)
        onValueChange?.(next)
      }}
      {...rest}
    >
      <PersistKeyContext.Provider value={persistKey}>{children}</PersistKeyContext.Provider>
    </RadixTabs.Root>
  )
})

/** Handed from a reorderable ``TabsList`` to its triggers. */
const ReorderContext = createContext<((value: string) => ReorderItemProps) | null>(null)

/**
 * Identifies a trigger for the saved order: its ``value``, or — for a
 * wrapper component that renders the ``TabsTrigger`` itself — its React key,
 * which then has to equal the value it passes down.
 */
function triggerId(child: ReactElement): string | null {
  const value = (child.props as { value?: unknown }).value
  return typeof value === 'string' ? value : child.key
}

export interface TabsListProps extends ComponentPropsWithoutRef<typeof RadixTabs.List> {
  /**
   * Lets the user drag the tabs into another order, saved in localStorage
   * under ``prism.tabs.<reorderKey>.order``. One key per set of tabs: lists
   * that share it read the same saved order when they mount. Every child must
   * be a ``TabsTrigger`` with a ``value``, or a wrapper keyed by that value;
   * anything else renders after the tabs. Defaults to the ``persistKey`` of
   * the enclosing ``TabsRoot``.
   */
  reorderKey?: string
}

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { className, reorderKey: ownReorderKey, children, ...rest },
  ref,
) {
  const persistKey = useContext(PersistKeyContext)
  const reorderKey = ownReorderKey ?? persistKey
  // ``Children.forEach`` hands over the elements as written, keys included
  // (``toArray`` would prefix them). ``null``/``false`` come through as
  // ``null`` and render as nothing among the others.
  const items: { id: string; node: ReactElement }[] = []
  const others: ReactNode[] = []
  Children.forEach(children, (child) => {
    const id = isValidElement(child) ? triggerId(child) : null
    if (id !== null && isValidElement(child)) items.push({ id, node: child })
    else others.push(child)
  })

  const { orderedIds, getItemProps, containerProps } = useReorderable(
    reorderKey === undefined ? undefined : `prism.tabs.${reorderKey}.order`,
    items.map((i) => i.id),
    'x',
  )

  if (reorderKey === undefined) {
    return (
      <RadixTabs.List ref={ref} className={cn(styles.list, className)} {...rest}>
        {children}
      </RadixTabs.List>
    )
  }

  const byId = new Map(items.map((i) => [i.id, i.node]))
  return (
    <ReorderContext.Provider value={getItemProps}>
      <RadixTabs.List
        ref={ref}
        className={cn(styles.list, className)}
        {...containerProps}
        {...rest}
      >
        {orderedIds.map((id) => (
          <Fragment key={id}>{byId.get(id)}</Fragment>
        ))}
        {others}
      </RadixTabs.List>
    </ReorderContext.Provider>
  )
})

export interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof RadixTabs.Trigger> {
  leadingIcon?: ReactNode
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(
  { className, leadingIcon, children, value, ...rest },
  ref,
) {
  const getReorderProps = useContext(ReorderContext)
  return (
    <RadixTabs.Trigger
      ref={ref}
      value={value}
      className={cn(styles.trigger, className)}
      {...getReorderProps?.(value)}
      {...rest}
    >
      {leadingIcon ? <span className={styles.icon} aria-hidden>{leadingIcon}</span> : null}
      <span className={styles.label}>{children}</span>
    </RadixTabs.Trigger>
  )
})

export const TabsContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(function TabsContent({ className, ...rest }, ref) {
  return <RadixTabs.Content ref={ref} className={cn(styles.content, className)} {...rest} />
})
